import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import type { InstrumentSlot } from "../../../../src/lib/audio";
import { useStoreSelector } from "../../../../src/lib/store";
import { getLabActions } from "../labActions";
import { trackHasContent } from "../lib/patternHelpers";
import { trackColorHex, trackTintBackground } from "../lib/trackColors";
import { performanceStore } from "../store/performanceStore";
import { projectStore } from "../store/projectStore";
import { patchUi, uiStore } from "../store/uiStore";

const STORAGE_KEY = "audio-lab:performance-modal";
const DEFAULT_WIDTH = 720;
const DEFAULT_HEIGHT = 480;
const MIN_WIDTH = 420;
const MIN_HEIGHT = 280;
/** Sticky pattern column width — used when scrolling cells out from under it. */
const PATTERN_COL_PX = 152; // 9.5rem
const STICKY_HEADER_PX = 64;

type PanelLayout = { left: number; top: number; width: number; height: number };

function clampSize(width: number, height: number): { width: number; height: number } {
  const maxW = Math.max(MIN_WIDTH, window.innerWidth - 16);
  const maxH = Math.max(MIN_HEIGHT, window.innerHeight - 16);
  return {
    width: Math.min(maxW, Math.max(MIN_WIDTH, Math.round(width))),
    height: Math.min(maxH, Math.max(MIN_HEIGHT, Math.round(height))),
  };
}

function clampPos(pos: { left: number; top: number }, width: number, height: number): {
  left: number;
  top: number;
} {
  const maxLeft = Math.max(8, window.innerWidth - width - 8);
  const maxTop = Math.max(8, window.innerHeight - height - 8);
  return {
    left: Math.min(maxLeft, Math.max(8, pos.left)),
    top: Math.min(maxTop, Math.max(8, pos.top)),
  };
}

function defaultLayout(): PanelLayout {
  const size = clampSize(DEFAULT_WIDTH, DEFAULT_HEIGHT);
  const left = Math.max(12, (window.innerWidth - size.width) / 2);
  const top = Math.max(12, Math.min(64, window.innerHeight * 0.08));
  return { ...clampPos({ left, top }, size.width, size.height), ...size };
}

function loadLayout(): PanelLayout | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return undefined;
    }
    const parsed = JSON.parse(raw) as Partial<PanelLayout>;
    if (typeof parsed.left !== "number" || typeof parsed.top !== "number") {
      return undefined;
    }
    const size = clampSize(
      typeof parsed.width === "number" ? parsed.width : DEFAULT_WIDTH,
      typeof parsed.height === "number" ? parsed.height : DEFAULT_HEIGHT,
    );
    return { ...clampPos({ left: parsed.left, top: parsed.top }, size.width, size.height), ...size };
  } catch {
    return undefined;
  }
}

function saveLayout(layout: PanelLayout): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
  } catch {
    // ignore
  }
}

function clampLayout(layout: PanelLayout): PanelLayout {
  const size = clampSize(layout.width, layout.height);
  return { ...clampPos(layout, size.width, size.height), ...size };
}

/**
 * Scroll a Performance matrix cell into view, accounting for the sticky
 * pattern column (left) and sticky header (top) so selection isn't hidden.
 */
function scrollPerfCellIntoView(
  scroller: HTMLElement,
  cell: HTMLElement,
  options?: { leftInset?: number },
): void {
  const leftInset = options?.leftInset ?? 0;
  const scrollerRect = scroller.getBoundingClientRect();
  const cellRect = cell.getBoundingClientRect();

  let nextLeft = scroller.scrollLeft;
  let nextTop = scroller.scrollTop;

  const visibleLeft = scrollerRect.left + leftInset;
  const visibleRight = scrollerRect.right;
  const visibleTop = scrollerRect.top + STICKY_HEADER_PX;
  const visibleBottom = scrollerRect.bottom;

  if (cellRect.left < visibleLeft) {
    nextLeft -= visibleLeft - cellRect.left;
  } else if (cellRect.right > visibleRight) {
    nextLeft += cellRect.right - visibleRight;
  }

  if (cellRect.top < visibleTop) {
    nextTop -= visibleTop - cellRect.top;
  } else if (cellRect.bottom > visibleBottom) {
    nextTop += cellRect.bottom - visibleBottom;
  }

  const maxLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
  const maxTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
  nextLeft = Math.max(0, Math.min(maxLeft, nextLeft));
  nextTop = Math.max(0, Math.min(maxTop, nextTop));

  if (nextLeft !== scroller.scrollLeft || nextTop !== scroller.scrollTop) {
    scroller.scrollTo({ left: nextLeft, top: nextTop });
  }
}

/** Ring fills 0→1 over the active lane's pattern period (launch/stop boundary). */
function LaneProgressRing({ progress }: { progress: number }) {
  const p = Math.min(1, Math.max(0, progress));
  const r = 11;
  const circ = 2 * Math.PI * r;
  return (
    <svg
      className="pointer-events-none absolute inset-0 m-auto text-lab-accent"
      width={28}
      height={28}
      viewBox="0 0 28 28"
      aria-hidden
    >
      <circle
        cx={14}
        cy={14}
        r={r}
        fill="none"
        stroke="rgba(255,255,255,0.14)"
        strokeWidth={2.5}
      />
      <circle
        cx={14}
        cy={14}
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeDasharray={`${p * circ} ${circ}`}
        transform="rotate(-90 14 14)"
      />
    </svg>
  );
}

type PerfSelection =
  | { kind: "pattern"; patternId: string }
  | { kind: "cell"; patternId: string; track: number };

/**
 * Ableton-style Performance grid: rows = patterns, columns = tracks.
 * Row headers launch whole patterns; cells launch one track from one pattern.
 * Pending launches pulse until the previous lane material finishes.
 * Arrow keys move a selection outline; Enter launches / stops the selection.
 */
export function PerformanceModal() {
  const open = useStoreSelector(uiStore, s => s.performanceOpen);
  const clip = useStoreSelector(projectStore, s => s.clip);
  const instruments = useStoreSelector(projectStore, s => s.instruments);
  const trackColors = useStoreSelector(projectStore, s => s.trackColors);
  const trackMuted = useStoreSelector(uiStore, s => s.trackMuted);
  const trackSolo = useStoreSelector(uiStore, s => s.trackSolo);
  const perf = useStoreSelector(performanceStore, s => s);

  const panelRef = useRef<HTMLDivElement>(null);
  const matrixRef = useRef<HTMLDivElement>(null);
  const dragCleanupRef = useRef<(() => void) | undefined>(undefined);
  const [layout, setLayout] = useState<PanelLayout>(() => loadLayout() ?? defaultLayout());
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [selection, setSelection] = useState<PerfSelection | null>(null);

  const trackCount = instruments.length;
  const patterns = clip.patterns;

  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const patternsRef = useRef(patterns);
  patternsRef.current = patterns;
  const trackCountRef = useRef(trackCount);
  trackCountRef.current = trackCount;

  const trackNames = useMemo(() => {
    const pattern = patterns[0];
    return Array.from({ length: trackCount }, (_, i) => {
      const name = pattern?.tracks[i]?.name?.trim();
      return name || `I${i + 1}`;
    });
  }, [patterns, trackCount]);

  function focusPanel(): void {
    queueMicrotask(() => panelRef.current?.focus());
  }

  function selectPattern(patternId: string): void {
    setSelection({ kind: "pattern", patternId });
    focusPanel();
    queueMicrotask(() => {
      const scroller = matrixRef.current;
      const cell = scroller?.querySelector<HTMLElement>(
        `[data-perf-cell="${patternId}:pattern"]`,
      );
      if (scroller && cell) {
        scrollPerfCellIntoView(scroller, cell, { leftInset: 0 });
      }
    });
  }

  function selectCell(patternId: string, track: number): void {
    setSelection({ kind: "cell", patternId, track });
    focusPanel();
    queueMicrotask(() => {
      const scroller = matrixRef.current;
      const cell = scroller?.querySelector<HTMLElement>(
        `[data-perf-cell="${patternId}:${track}"]`,
      );
      if (scroller && cell) {
        // Keep cells clear of the sticky Pattern column.
        scrollPerfCellIntoView(scroller, cell, { leftInset: PATTERN_COL_PX });
      }
    });
  }

  function moveSelection(dx: number, dy: number): boolean {
    const pats = patternsRef.current;
    const tc = trackCountRef.current;
    if (pats.length === 0 || tc <= 0 || (dx === 0 && dy === 0)) {
      return false;
    }
    const patternIds = pats.map(p => p.id);
    let sel = selectionRef.current;
    if (!sel || !patternIds.includes(sel.patternId)) {
      sel = { kind: "cell", patternId: patternIds[0]!, track: 0 };
    }

    if (sel.kind === "pattern") {
      const patternIndex = patternIds.indexOf(sel.patternId);
      if (patternIndex < 0) {
        return false;
      }
      if (dx > 0) {
        selectCell(sel.patternId, 0);
        return true;
      }
      if (dy === 0) {
        return false;
      }
      const nextIndex = Math.max(0, Math.min(patternIds.length - 1, patternIndex + dy));
      if (nextIndex === patternIndex) {
        return false;
      }
      selectPattern(patternIds[nextIndex]!);
      return true;
    }

    const patternIndex = patternIds.indexOf(sel.patternId);
    if (patternIndex < 0) {
      return false;
    }
    if (dx < 0 && sel.track === 0) {
      selectPattern(sel.patternId);
      return true;
    }
    const nextTrack = Math.max(0, Math.min(tc - 1, sel.track + dx));
    const nextPatternIndex = Math.max(
      0,
      Math.min(patternIds.length - 1, patternIndex + dy),
    );
    if (nextTrack === sel.track && nextPatternIndex === patternIndex) {
      return false;
    }
    selectCell(patternIds[nextPatternIndex]!, nextTrack);
    return true;
  }

  function activateSelection(): boolean {
    const sel = selectionRef.current;
    const pats = patternsRef.current;
    if (!sel) {
      return false;
    }
    const pattern = pats.find(p => p.id === sel.patternId);
    if (!pattern) {
      return false;
    }
    if (sel.kind === "pattern") {
      getLabActions()?.launchPerformancePattern?.(sel.patternId);
      return true;
    }
    const cells = pattern.tracks[sel.track]?.cells;
    const lane = performanceStore.getValue().lanes[sel.track];
    const active = lane?.activePatternId === sel.patternId;
    const pending =
      (lane?.pending?.kind === "launch" && lane.pending.patternId === sel.patternId)
      || (active && lane?.pending?.kind === "stop");
    if (!trackHasContent(cells) && !active && !pending) {
      return false;
    }
    getLabActions()?.launchPerformanceCell?.(sel.patternId, sel.track as InstrumentSlot);
    return true;
  }

  useEffect(() => {
    if (!open) {
      return;
    }
    setLayout(prev => {
      const next = clampLayout(loadLayout() ?? prev);
      saveLayout(next);
      return next;
    });
    const firstId = projectStore.getValue().clip.patterns[0]?.id;
    if (firstId) {
      setSelection(prev => {
        if (prev && projectStore.getValue().clip.patterns.some(p => p.id === prev.patternId)) {
          return prev;
        }
        return { kind: "cell", patternId: firstId, track: 0 };
      });
    }
    focusPanel();
  }, [open]);

  // Keep selection valid when patterns / track count change.
  useEffect(() => {
    if (!open || patterns.length === 0 || trackCount <= 0) {
      return;
    }
    setSelection(prev => {
      if (!prev) {
        return { kind: "cell", patternId: patterns[0]!.id, track: 0 };
      }
      const patternOk = patterns.some(p => p.id === prev.patternId);
      if (!patternOk) {
        return { kind: "cell", patternId: patterns[0]!.id, track: 0 };
      }
      if (prev.kind === "cell" && prev.track >= trackCount) {
        return { kind: "cell", patternId: prev.patternId, track: trackCount - 1 };
      }
      return prev;
    });
  }, [open, patterns, trackCount]);

  useEffect(() => {
    if (!open) {
      return;
    }
    function onWinResize(): void {
      setLayout(prev => {
        const next = clampLayout(prev);
        saveLayout(next);
        return next;
      });
    }
    function onKey(event: KeyboardEvent): void {
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable=true]")) {
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        close();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        event.stopPropagation();
        moveSelection(0, -1);
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        event.stopPropagation();
        moveSelection(0, 1);
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        event.stopPropagation();
        moveSelection(-1, 0);
        return;
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        event.stopPropagation();
        moveSelection(1, 0);
        return;
      }
      if (event.key === "Enter" && !event.repeat) {
        event.preventDefault();
        event.stopPropagation();
        activateSelection();
      }
    }
    window.addEventListener("resize", onWinResize);
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("resize", onWinResize);
      window.removeEventListener("keydown", onKey, true);
      dragCleanupRef.current?.();
      dragCleanupRef.current = undefined;
    };
  }, [open]);

  function close(): void {
    dragCleanupRef.current?.();
    dragCleanupRef.current = undefined;
    setDragging(false);
    setResizing(false);
    patchUi({ performanceOpen: false });
    getLabActions()?.setStatus?.("Performance closed.");
  }

  function beginDrag(event: ReactPointerEvent): void {
    if (event.button !== 0) {
      return;
    }
    const targetEl = event.target as HTMLElement | null;
    if (targetEl?.closest("button, input, select, textarea, label")) {
      return;
    }
    event.preventDefault();
    const el = panelRef.current;
    if (!el) {
      return;
    }
    dragCleanupRef.current?.();
    const rect = el.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    setDragging(true);
    const onMove = (moveEvent: PointerEvent): void => {
      setLayout(prev => {
        const pos = clampPos(
          { left: moveEvent.clientX - offsetX, top: moveEvent.clientY - offsetY },
          prev.width,
          prev.height,
        );
        const next = { ...prev, ...pos };
        saveLayout(next);
        return next;
      });
    };
    const onUp = (): void => {
      setDragging(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      dragCleanupRef.current = undefined;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    dragCleanupRef.current = onUp;
  }

  function beginResize(event: ReactPointerEvent): void {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    dragCleanupRef.current?.();
    const startX = event.clientX;
    const startY = event.clientY;
    const startW = layout.width;
    const startH = layout.height;
    const startLeft = layout.left;
    const startTop = layout.top;
    setResizing(true);
    const onMove = (moveEvent: PointerEvent): void => {
      const size = clampSize(startW + (moveEvent.clientX - startX), startH + (moveEvent.clientY - startY));
      const pos = clampPos({ left: startLeft, top: startTop }, size.width, size.height);
      const next = { ...pos, ...size };
      saveLayout(next);
      setLayout(next);
    };
    const onUp = (): void => {
      setResizing(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      dragCleanupRef.current = undefined;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    dragCleanupRef.current = onUp;
  }

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      ref={panelRef}
      className={`help-modal performance-modal fixed z-50 flex flex-col overflow-hidden rounded-lg border border-lab-line bg-lab-panel text-[12px] text-lab-text shadow-[0_24px_48px_rgba(0,0,0,0.45)] outline-none${
        dragging || resizing ? " select-none" : ""
      }`}
      role="dialog"
      aria-modal="false"
      aria-label="Performance"
      tabIndex={-1}
      style={{
        left: layout.left,
        top: layout.top,
        width: layout.width,
        height: layout.height,
      }}
    >
      <div
        className="help-modal-header flex shrink-0 cursor-move touch-none select-none items-center gap-2 border-b border-lab-line bg-lab-panel-bar px-[0.65rem] py-1.5"
        onPointerDown={beginDrag}
      >
        <span className="help-modal-title shrink-0 text-[12px] font-semibold text-lab-text">
          Performance
        </span>
        <div
          className="inline-flex select-none items-baseline gap-[0.35rem] rounded border border-lab-line bg-lab-bg px-[0.55rem] py-[0.28rem] font-lab-mono text-[12px] leading-[1.2] tracking-[0.02em] text-lab-text tabular-nums"
          title="Performance playhead row / quantum"
          aria-live="polite"
          onPointerDown={event => event.stopPropagation()}
        >
          <span className="min-w-[2ch] text-right text-slate-200">
            {perf.playing && perf.playheadRow != null
              ? String(perf.playheadRow).padStart(2, "0")
              : "--"}
          </span>
          <span className="text-lab-muted opacity-70" aria-hidden>
            /
          </span>
          <span className="min-w-[2ch] text-left text-lab-muted">
            {String(Math.max(1, perf.quantumSteps)).padStart(2, "0")}
          </span>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            className={`tb-icon${perf.playing ? " active" : ""}`}
            title={perf.playing ? "Playing" : "Play"}
            aria-label="Play"
            aria-pressed={perf.playing}
            onPointerDown={event => event.stopPropagation()}
            onClick={() => getLabActions()?.startPerformance?.()}
          >
            <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden>
              <path d="M8 5.14v13.72L19 12 8 5.14z" fill="currentColor" />
            </svg>
          </button>
          <button
            type="button"
            className="tb-icon"
            title="Stop"
            aria-label="Stop"
            onPointerDown={event => event.stopPropagation()}
            onClick={() => getLabActions()?.stopPerformance?.()}
          >
            <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden>
              <rect x="6" y="6" width="12" height="12" rx="1.5" fill="currentColor" />
            </svg>
          </button>
          <button
            type="button"
            className={`tb-icon${perf.recording ? " active record" : ""}`}
            title={
              perf.recording
                ? "Stop recording → save WAV to Samples / performances"
                : "Record Performance mix → Samples / performances"
            }
            aria-label="Record Performance"
            aria-pressed={perf.recording}
            onPointerDown={event => event.stopPropagation()}
            onClick={() => void getLabActions()?.togglePerformanceRecord?.()}
          >
            <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden>
              <circle cx="12" cy="12" r="6" fill="currentColor" />
            </svg>
          </button>
          <button
            type="button"
            className="help-modal-close rounded border border-transparent bg-transparent px-2 py-[0.2rem] text-[12px] text-lab-muted hover:border-lab-line hover:bg-white/[0.04] hover:text-lab-text"
            title="Close"
            aria-label="Close"
            onPointerDown={event => event.stopPropagation()}
            onClick={close}
          >
            Close
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-2.5 pt-2 pb-2">
        <div ref={matrixRef} className="min-h-0 flex-1 overflow-auto rounded border border-lab-line">
          <table className="w-max table-fixed border-collapse text-[11px]">
            <colgroup>
              <col className="w-[9.5rem]" />
              {Array.from({ length: trackCount }, (_, i) => (
                <col key={i} className="w-[6.75rem]" />
              ))}
            </colgroup>
            <thead className="performance-matrix-head sticky top-0 z-[1]">
              <tr>
                <th
                  className="sticky left-0 z-[2] w-[9.5rem] border-b border-r border-white/[0.12] px-2 py-1.5 text-left font-semibold text-slate-300"
                  style={{ backgroundColor: "var(--perf-header)" }}
                >
                  Pattern
                </th>
                {trackNames.map((name, i) => {
                  const tint = trackColorHex(trackColors[i]);
                  const muted = trackMuted[i] === true;
                  const solo = trackSolo[i] === true;
                  const mutedLook = muted && !solo;
                  const showTint = Boolean(tint) && !mutedLook;
                  const lane = perf.lanes[i];
                  const laneBusy =
                    lane?.activePatternId != null || lane?.pending != null;
                  const pendingStop = lane?.pending?.kind === "stop";
                  return (
                    <th
                      key={i}
                      className={[
                        "w-[6.75rem] border-b border-r px-1 py-1 text-center font-semibold",
                        mutedLook
                          ? "border-white/[0.06] text-lab-muted/80"
                          : "border-white/[0.12]",
                        !mutedLook && showTint
                          ? "text-slate-100"
                          : !mutedLook
                            ? "text-slate-300"
                            : "",
                      ].join(" ")}
                      style={{
                        backgroundColor: showTint
                          ? trackTintBackground("var(--arrange-tint-base)", trackColors[i], 34)
                          : mutedLook
                            ? "var(--perf-header-muted, #12161c)"
                            : "var(--perf-header)",
                        boxShadow: showTint
                          ? `inset 0 -2px 0 ${tint}`
                          : mutedLook
                            ? undefined
                            : "inset 0 -1px 0 rgba(255, 255, 255, 0.08)",
                      }}
                    >
                      <span
                        className={[
                          "rounded border px-1 py-px text-[9px]",
                          showTint
                            ? "border-white/25"
                            : mutedLook
                              ? "border-white/10 text-lab-muted/70"
                              : "border-white/18 bg-black/35 text-slate-200",
                        ].join(" ")}
                      >
                        I{i + 1}
                      </span>
                      <div
                        className={[
                          "mt-0.5 truncate font-normal normal-case",
                          mutedLook ? "line-through decoration-lab-muted/70" : "",
                        ].join(" ")}
                      >
                        {name}
                      </div>
                      <div className="mt-1 flex items-center justify-center gap-0.5">
                        <button
                          type="button"
                          title="Solo"
                          aria-label={`Solo I${i + 1}`}
                          aria-pressed={solo}
                          className={`inline-grid h-[1.35rem] w-[1.35rem] place-items-center rounded border p-0 text-[9px] leading-none font-bold ${
                            solo
                              ? "border-[#e6b35a] bg-[#e6b35a] text-slate-900"
                              : "border-lab-line bg-lab-panel text-slate-500 hover:border-white/30 hover:text-slate-200"
                          }`}
                          onClick={() => getLabActions()?.toggleSolo?.(i as InstrumentSlot)}
                        >
                          S
                        </button>
                        <button
                          type="button"
                          title="Mute"
                          aria-label={`Mute I${i + 1}`}
                          aria-pressed={muted}
                          className={`inline-grid h-[1.35rem] w-[1.35rem] place-items-center rounded border p-0 text-[9px] leading-none font-bold ${
                            muted
                              ? "border-slate-400 bg-slate-400 text-slate-900"
                              : "border-lab-line bg-lab-panel text-slate-500 hover:border-white/30 hover:text-slate-200"
                          }`}
                          onClick={() => getLabActions()?.toggleMute?.(i as InstrumentSlot)}
                        >
                          M
                        </button>
                        <button
                          type="button"
                          title={
                            pendingStop
                              ? "Cancel stop"
                              : "Stop column at end of cycle"
                          }
                          aria-label={`Stop I${i + 1} at end of cycle`}
                          aria-pressed={pendingStop}
                          disabled={!laneBusy}
                          className={`inline-grid h-[1.35rem] w-[1.35rem] place-items-center rounded border p-0 text-[9px] leading-none ${
                            pendingStop
                              ? "performance-pulse border-rose-400/70 bg-rose-500/25 text-rose-100"
                              : laneBusy
                                ? "border-lab-line bg-lab-panel text-slate-400 hover:border-white/30 hover:text-slate-100"
                                : "cursor-default border-transparent bg-transparent text-lab-muted/35"
                          }`}
                          onClick={() =>
                            getLabActions()?.stopPerformanceLane?.(i as InstrumentSlot)
                          }
                        >
                          ◼
                        </button>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {patterns.map(pattern => {
                const patternLabel = pattern.name?.trim() || pattern.id;
                const contentTracks = Array.from({ length: trackCount }, (_, t) => t).filter(
                  t => trackHasContent(pattern.tracks[t]?.cells),
                );
                const allActive =
                  contentTracks.length > 0
                  && contentTracks.every(t => perf.lanes[t]?.activePatternId === pattern.id);
                const anyPending = contentTracks.some(t => {
                  const pending = perf.lanes[t]?.pending;
                  return pending?.kind === "launch" && pending.patternId === pattern.id;
                });

                return (
                  <tr key={pattern.id}>
                    <th
                      className="sticky left-0 z-[1] w-[9.5rem] border-b border-r border-lab-line px-1 py-1 text-left font-normal"
                      style={{ backgroundColor: "var(--arrange-row-a)" }}
                    >
                      <button
                        type="button"
                        data-perf-cell={`${pattern.id}:pattern`}
                        title={`Launch pattern ${patternLabel}`}
                        className={[
                          "w-full rounded border px-2 py-1.5 text-left text-[11px] font-semibold",
                          allActive
                            ? "border-lab-accent bg-[rgba(61,139,253,0.28)] text-white"
                            : "border-lab-line bg-lab-panel-deep text-lab-text hover:border-white/30",
                          anyPending ? "performance-pulse" : "",
                          selection?.kind === "pattern" && selection.patternId === pattern.id
                            ? "z-[1] outline outline-1 outline-lab-accent -outline-offset-1"
                            : "",
                        ].join(" ")}
                        onPointerDown={() => selectPattern(pattern.id)}
                        onClick={() => {
                          selectPattern(pattern.id);
                          getLabActions()?.launchPerformancePattern?.(pattern.id);
                        }}
                      >
                        <span className="block truncate">{patternLabel}</span>
                        <span className="block text-[9px] font-normal text-lab-muted">
                          {pattern.id} · {pattern.steps} steps
                        </span>
                      </button>
                    </th>
                    {Array.from({ length: trackCount }, (_, track) => {
                      const cells = pattern.tracks[track]?.cells;
                      const hasContent = trackHasContent(cells);
                      const lane = perf.lanes[track];
                      const active = lane?.activePatternId === pattern.id;
                      const pendingLaunch =
                        lane?.pending?.kind === "launch"
                        && lane.pending.patternId === pattern.id;
                      const pendingStop =
                        active
                        && lane?.pending?.kind === "stop";
                      const pending = pendingLaunch || pendingStop;
                      const period = Math.max(1, pattern.steps || 1);
                      const phase =
                        active && perf.playing && perf.playheadRow != null
                          ? ((perf.playheadRow % period) + 1) / period
                          : 0;
                      const selected =
                        selection?.kind === "cell"
                        && selection.patternId === pattern.id
                        && selection.track === track;

                      return (
                        <td
                          key={track}
                          className="w-[6.75rem] border-b border-lab-line p-1"
                          onPointerDown={() => selectCell(pattern.id, track)}
                        >
                          <button
                            type="button"
                            data-perf-cell={`${pattern.id}:${track}`}
                            disabled={!hasContent && !active && !pending}
                            title={
                              hasContent
                                ? `Launch ${pattern.id} track I${track + 1}`
                                : undefined
                            }
                            className={[
                              "relative flex h-10 w-full items-center justify-center rounded border text-[11px] font-semibold transition-colors",
                              !hasContent && !active && !pending
                                ? "cursor-default border-transparent bg-transparent text-lab-muted/40"
                                : active
                                  ? "border-lab-accent bg-[rgba(61,139,253,0.3)] text-white"
                                  : "border-lab-line bg-lab-panel-deep text-lab-text hover:border-white/35 hover:bg-white/[0.04]",
                              pending ? "performance-pulse" : "",
                              selected
                                ? "z-[1] outline outline-1 outline-lab-accent -outline-offset-1"
                                : "",
                            ].join(" ")}
                            onClick={() => {
                              selectCell(pattern.id, track);
                              if (!hasContent && !active && !pending) {
                                return;
                              }
                              getLabActions()?.launchPerformanceCell?.(
                                pattern.id,
                                track as InstrumentSlot,
                              );
                            }}
                          >
                            {active && perf.playing ? <LaneProgressRing progress={phase} /> : null}
                            <span className="relative z-[1]">
                              {pendingStop ? "◼" : pendingLaunch ? "►" : active ? "●" : hasContent ? "○" : ""}
                            </span>
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div
        role="separator"
        aria-label="Resize performance"
        title="Resize"
        className="absolute right-0 bottom-0 z-10 h-4 w-4 cursor-nwse-resize touch-none"
        onPointerDown={beginResize}
      >
        <svg
          viewBox="0 0 16 16"
          width={14}
          height={14}
          className="absolute right-0.5 bottom-0.5 text-lab-muted opacity-70"
          aria-hidden
        >
          <path
            d="M5 15h2M9 15h2M13 11v2M13 7v2M13 15V13M11 15h2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </div>
    </div>,
    document.body,
  );
}
